import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { doc, setDoc, getDoc, updateDoc, arrayUnion, arrayRemove, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';
import { Product } from '../types';
import { getProduct } from '../firebase/firestore';
import toast from 'react-hot-toast';

interface WishlistContextType {
  wishlistIds: string[];
  wishlistProducts: Product[];
  loading: boolean;
  addToWishlist: (productId: string) => Promise<void>;
  removeFromWishlist: (productId: string) => Promise<void>;
  isInWishlist: (productId: string) => boolean;
  clearWishlist: () => Promise<void>;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const useWishlist = (): WishlistContextType => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within WishlistProvider');
  }
  return context;
};

interface WishlistProviderProps {
  children: ReactNode;
}

export const WishlistProvider: React.FC<WishlistProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [wishlistIds, setWishlistIds] = useState<string[]>([]);
  const [wishlistProducts, setWishlistProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!currentUser || !db) {
      setWishlistIds([]);
      return;
    }

    const loadWishlist = async () => {
      try {
        const wishlistRef = doc(db!, 'wishlists', currentUser.id);
        const wishlistSnap = await getDoc(wishlistRef);
        
        if (wishlistSnap.exists()) {
          const data = wishlistSnap.data();
          const items = data.items || [];
          const validIds = items.filter(
            (id: any) => id && typeof id === 'string' && id.trim() !== ''
          );
          setWishlistIds(validIds);
        } else {
          setWishlistIds([]);
        }
      } catch (error) {
        console.error('Error loading wishlist:', error);
        setWishlistIds([]);
      }
    };

    loadWishlist();
  }, [currentUser]);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const fetchWishlistProducts = async () => {
      if (wishlistIds.length === 0) {
        if (isMounted) setWishlistProducts([]);
        return;
      }

      if (isMounted) setLoading(true);
      try {
        const validIds = wishlistIds.filter(
          id => id && typeof id === 'string' && id.trim() !== ''
        );

        if (validIds.length === 0) {
          if (isMounted) setWishlistProducts([]);
          if (isMounted) setLoading(false);
          return;
        }

        const products = await Promise.all(
          validIds.map(id => getProduct(id))
        );
        
        if (isMounted) {
          const validProducts = products.filter((p: Product | null): p is Product => p !== null);
          setWishlistProducts(validProducts);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching wishlist products:', error);
          setWishlistProducts([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchWishlistProducts();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [wishlistIds]);

  const addToWishlist = useCallback(async (productId: string): Promise<void> => {
    if (!currentUser || !db) {
      toast.error('Please login to add items to wishlist');
      return;
    }

    if (wishlistIds.includes(productId)) {
      toast.error('Already in wishlist');
      return;
    }

    const updatedIds = [...wishlistIds, productId];
    setWishlistIds(updatedIds);

    try {
      const wishlistRef = doc(db!, 'wishlists', currentUser.id);
      const wishlistSnap = await getDoc(wishlistRef);
      
      if (wishlistSnap.exists()) {
        await updateDoc(wishlistRef, {
          items: arrayUnion(productId),
          updatedAt: serverTimestamp()
        });
      } else {
        await setDoc(wishlistRef, {
          userId: currentUser.id,
          items: [productId],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }

      toast.success('Added to wishlist');
    } catch (error) {
      setWishlistIds(wishlistIds);
      console.error('Error adding to wishlist:', error);
      toast.error('Failed to add to wishlist');
    }
  }, [currentUser, wishlistIds]);

  const removeFromWishlist = useCallback(async (productId: string): Promise<void> => {
    if (!currentUser || !db) return;

    const updatedIds = wishlistIds.filter(id => id !== productId);
    setWishlistIds(updatedIds);

    try {
      const wishlistRef = doc(db!, 'wishlists', currentUser.id);
      await updateDoc(wishlistRef, {
        items: arrayRemove(productId),
        updatedAt: serverTimestamp()
      });

      toast.success('Removed from wishlist');
    } catch (error) {
      setWishlistIds(wishlistIds);
      console.error('Error removing from wishlist:', error);
      toast.error('Failed to remove from wishlist');
    }
  }, [currentUser, wishlistIds]);

  const isInWishlist = useCallback((productId: string): boolean => {
    return wishlistIds.includes(productId);
  }, [wishlistIds]);

  const clearWishlist = useCallback(async (): Promise<void> => {
    if (!currentUser || !db) return;

    const oldIds = [...wishlistIds];
    
    setWishlistIds([]);

    try {
      const wishlistRef = doc(db!, 'wishlists', currentUser.id);
      await updateDoc(wishlistRef, {
        items: [],
        updatedAt: serverTimestamp()
      });

      toast.success('Wishlist cleared');
    } catch (error) {
      setWishlistIds(oldIds);
      console.error('Error clearing wishlist:', error);
      toast.error('Failed to clear wishlist');
    }
  }, [currentUser, wishlistIds]);

  const value = useMemo(() => ({
    wishlistIds,
    wishlistProducts,
    loading,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    clearWishlist
  }), [wishlistIds, wishlistProducts, loading, addToWishlist, removeFromWishlist, isInWishlist, clearWishlist]);

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};
